import { IEspActivity } from '../model/types'

/** HP's fiscal year starts on 1 November: FY27 runs from November 2026 to October 2027. */
const FY_START_MONTH = 10

export interface IEspFiscalQuarter {
  fYear: number
  quarter: number
  /** `2027-Q1`, for comparing and keying. */
  key: string
  /** `Q1 FY27`. */
  label: string
  start: Date
  /** The first day of the next quarter. */
  end: Date
}

const toQuarter = (fYear: number, quarter: number): IEspFiscalQuarter => ({
  fYear,
  quarter,
  key: `${fYear}-Q${quarter}`,
  label: `Q${quarter} FY${String(fYear % 100).padStart(2, '0')}`,
  start: new Date(fYear - 1, FY_START_MONTH + (quarter - 1) * 3, 1),
  end: new Date(fYear - 1, FY_START_MONTH + quarter * 3, 1),
})

export const getEspFiscalQuarter = (date: Date): IEspFiscalQuarter => {
  const month = date.getMonth()
  const fYear = date.getFullYear() + (month >= FY_START_MONTH ? 1 : 0)
  const fiscalMonth = (month + 12 - FY_START_MONTH) % 12

  return toQuarter(fYear, Math.floor(fiscalMonth / 3) + 1)
}

export const shiftEspFiscalQuarter = (from: IEspFiscalQuarter, by: number): IEspFiscalQuarter => {
  const index = from.fYear * 4 + from.quarter - 1 + by

  return toQuarter(Math.floor(index / 4), (index % 4) + 1)
}

/** An activity's `yyyy-MM-dd` as a local date: `new Date(iso)` reads it as UTC midnight, which is
 *  the day before west of Greenwich. */
export const parseEspDate = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number)

  return new Date(year, month - 1, day)
}

export const getEspActivityQuarter = (activity: Pick<IEspActivity, 'date'>) =>
  getEspFiscalQuarter(parseEspDate(activity.date))

/** The engagement already logged in the quarter of `date`, the edited one aside: the program allows
 *  one per partner and quarter, counted by the date of the meeting. */
export const findEspQuarterActivity = <T extends Pick<IEspActivity, 'id' | 'date'>>(
  activities: T[],
  date: Date,
  exceptId?: string | null
): T | null => {
  const { key } = getEspFiscalQuarter(date)

  return (
    activities.find(
      (activity) => activity.id !== exceptId && getEspActivityQuarter(activity).key === key
    ) ?? null
  )
}
